import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RealTimeAlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');

  const mockAlerts = [
    {
      id: 1,
      type: 'urgent',
      category: 'coverage',
      title: 'Kritische Unterbesetzung',
      message: 'Fertigungsabteilung hat nur 2 von 4 benötigten Mitarbeitern für die Nachtschicht (22:00-06:00)',
      timestamp: new Date(Date.now() - 300000), // 5 minutes ago
      department: 'Manufacturing',
      shift: '22:00-06:00',
      status: 'active',
      priority: 'high'
    },
    {
      id: 2,
      type: 'warning',
      category: 'conflict',
      title: 'Schichtkonflikt erkannt',
      message: 'Anna Müller ist für überlappende Schichten in Customer Service eingeteilt',
      timestamp: new Date(Date.now() - 600000), // 10 minutes ago
      department: 'Customer Service',
      employee: 'Anna Müller',
      status: 'active',
      priority: 'medium'
    },
    {
      id: 3,
      type: 'info',
      category: 'availability',
      title: 'Verfügbarkeitsänderung',
      message: 'Thomas Weber hat seine Verfügbarkeit für nächste Woche aktualisiert',
      timestamp: new Date(Date.now() - 900000), // 15 minutes ago
      department: 'Operations',
      employee: 'Thomas Weber',
      status: 'resolved',
      priority: 'low'
    },
    {
      id: 4,
      type: 'urgent',
      category: 'absence',
      title: 'Kurzfristige Krankmeldung',
      message: 'Maria Schmidt hat sich für heute krank gemeldet. Schicht 14:00-22:00 muss neu besetzt werden',
      timestamp: new Date(Date.now() - 1200000), // 20 minutes ago
      department: 'Customer Service',
      employee: 'Maria Schmidt',
      shift: '14:00-22:00',
      status: 'active',
      priority: 'high'
    },
    {
      id: 5,
      type: 'warning',
      category: 'overtime',
      title: 'Überstunden-Warnung',
      message: 'Klaus Fischer nähert sich der wöchentlichen Überstunden-Grenze (38/40 Stunden)',
      timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
      department: 'Operations',
      employee: 'Klaus Fischer',
      status: 'active',
      priority: 'medium'
    },
    {
      id: 6,
      type: 'success',
      category: 'coverage',
      title: 'Schicht erfolgreich besetzt',
      message: 'Offene Schicht in Administration wurde durch Lisa Wagner übernommen',
      timestamp: new Date(Date.now() - 2400000), // 40 minutes ago
      department: 'Administration',
      employee: 'Lisa Wagner',
      status: 'resolved',
      priority: 'low'
    }
  ];

  useEffect(() => {
    setAlerts(mockAlerts);
  }, []);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'urgent':
        return 'AlertTriangle';
      case 'warning':
        return 'AlertCircle';
      case 'info':
        return 'Info';
      case 'success':
        return 'CheckCircle';
      default:
        return 'Bell';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'urgent':
        return 'text-error bg-error/10 border-error/20';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'info':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'success':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error';
      case 'medium':
        return 'bg-warning';
      case 'low':
        return 'bg-success';
      default:
        return 'bg-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min`;
    if (hours < 24) return `vor ${hours} Std`;
    return timestamp?.toLocaleDateString('de-DE');
  };

  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts?.filter(alert => alert?.status === filter);

  const activeAlertsCount = alerts?.filter(alert => alert?.status === 'active')?.length;
  const urgentAlertsCount = alerts?.filter(alert => alert?.type === 'urgent' && alert?.status === 'active')?.length;

  const handleResolveAlert = (alertId) => {
    setAlerts(alerts?.map(alert => 
      alert?.id === alertId 
        ? { ...alert, status: 'resolved' }
        : alert
    ));
  };

  const handleDismissAlert = (alertId) => {
    setAlerts(alerts?.filter(alert => alert?.id !== alertId));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center relative">
            <Icon name="Bell" size={18} className="text-warning" />
            {activeAlertsCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center">
                {activeAlertsCount}
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-foreground">Echtzeit-Warnungen</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={filter === 'all' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => setFilter('all')}
          >
            Alle
          </Button>
          <Button
            variant={filter === 'active' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => setFilter('active')}
          >
            Aktiv ({activeAlertsCount})
          </Button>
          <Button
            variant={filter === 'resolved' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => setFilter('resolved')}
          >
            Gelöst
          </Button>
        </div>
      </div>
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {filteredAlerts?.length > 0 ? (
          filteredAlerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`p-4 rounded-lg border transition-all hover:shadow-card ${getAlertColor(alert?.type)} ${
                alert?.status === 'resolved' ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name={getAlertIcon(alert?.type)} size={16} />
                  <h4 className="font-medium text-sm">{alert?.title}</h4>
                  <div className={`w-2 h-2 rounded-full ${getPriorityColor(alert?.priority)}`} />
                </div>
                <div className="flex items-center space-x-1">
                  {alert?.status === 'active' && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6"
                        onClick={() => handleResolveAlert(alert?.id)}
                      >
                        <Icon name="Check" size={12} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6"
                        onClick={() => handleDismissAlert(alert?.id)}
                      >
                        <Icon name="X" size={12} />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <p className="text-sm mb-3">{alert?.message}</p>

              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-4">
                  <span className="text-muted-foreground">
                    {alert?.department}
                  </span>
                  {alert?.employee && (
                    <span className="text-muted-foreground">
                      {alert?.employee}
                    </span>
                  )}
                  {alert?.shift && (
                    <span className="text-muted-foreground">
                      {alert?.shift}
                    </span>
                  )}
                </div>
                <span className="text-muted-foreground">
                  {formatTimestamp(alert?.timestamp)}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-4 opacity-50" />
            <p>Keine Warnungen gefunden</p>
          </div>
        )}
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center text-xs">
          <div>
            <div className="text-lg font-semibold text-error">{urgentAlertsCount}</div>
            <div className="text-muted-foreground">Dringend</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-warning">
              {alerts?.filter(a => a?.type === 'warning' && a?.status === 'active')?.length}
            </div>
            <div className="text-muted-foreground">Warnungen</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-success">
              {alerts?.filter(a => a?.status === 'resolved')?.length}
            </div>
            <div className="text-muted-foreground">Gelöst</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeAlertsPanel;